import { Button, ConfigProvider, DatePicker, Space, Table, Tag } from "antd";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import React, { useEffect, useMemo, useState } from "react";
import Footer from "../../../components/Footer";
import ListControls from "../../../components/ListControls";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";

const { RangePicker } = DatePicker;
const TRANSACTIONS_ENDPOINT = "/api/mobile/tontine/transactions";
const EXPORT_ENDPOINT = (format) =>
  `/api/mobile/tontine/transactions/export/${format}`;

const mapStatusForBackend = (status) => {
  const normalized = (status || "").toString().toUpperCase();
  switch (normalized) {
    case "PAID":
    case "SUCCESS":
    case "APPROVED":
      return "APPROVED";
    case "ERROR":
    case "FAILED":
      return "FAILED";
    case "PENDING":
    case "EXPIRED":
    case "CANCELLED":
      return normalized;
    default:
      return undefined;
  }
};

const normalizeForSearch = (value) => {
  if (value === null || typeof value === "undefined") return "";
  if (typeof value === "object") {
    const resolved =
      value.fullName ||
      value.displayName ||
      value.nom ||
      value.prenom ||
      value.username;
    const asString = resolved ? String(resolved) : JSON.stringify(value);
    return asString.toLowerCase();
  }
  return String(value).toLowerCase();
};

const TransactionsTontine = () => {
  const [loading, setLoading] = useState(false);
  const [datas, setDatas] = useState([]);
  const [dates, setDates] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState();

  const fetchDatas = (overrides = {}) => {
    const effectiveDates = overrides.dates ?? dates;
    const effectiveStatus = overrides.status ?? statusFilter;
    const apiStatus = mapStatusForBackend(effectiveStatus);

    console.log("[TransactionsTontine] Fetching with params", {
      startDate: effectiveDates?.[0]?.format?.("YYYY-MM-DD"),
      endDate: effectiveDates?.[1]?.format?.("YYYY-MM-DD"),
      status: apiStatus,
    });

    setLoading(true);
    axiosInstance
      .get(TRANSACTIONS_ENDPOINT, {
        params: {
          startDate: effectiveDates?.[0] ? effectiveDates[0].format("YYYY-MM-DD") : undefined,
          endDate: effectiveDates?.[1] ? effectiveDates[1].format("YYYY-MM-DD") : undefined,
          status: apiStatus,
        },
      })
      .then((res) => {
        const nextData = Array.isArray(res.data) ? res.data : [];
        console.log("[TransactionsTontine] Received rows:", nextData.length);
        if (!Array.isArray(res.data)) {
          console.log("[TransactionsTontine] Unexpected response shape", res.data);
        }
        setDatas(nextData);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchDatas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = (format) => {
    const effectiveDates = dates;
    const effectiveStatus = statusFilter;
    const apiStatus = mapStatusForBackend(effectiveStatus);

    axiosInstance
      .get(EXPORT_ENDPOINT(format), {
        params: {
          startDate: effectiveDates?.[0] ? effectiveDates[0].format("YYYY-MM-DD") : undefined,
          endDate: effectiveDates?.[1] ? effectiveDates[1].format("YYYY-MM-DD") : undefined,
          status: apiStatus,
        },
        responseType: "blob",
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `transactions.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((err) => console.log(err));
  };

  const formatDateTime = (value) =>
    value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-";

  const formatAmount = (value) =>
    typeof value === "number"
      ? new Intl.NumberFormat("fr-FR").format(value)
      : value || "-";

  const resolveText = (value) => {
    if (value === null || typeof value === "undefined") {
      return "-";
    }
    if (typeof value === "object") {
      return (
        value.fullName ||
        value.displayName ||
        value.nom ||
        value.prenom ||
        value.username ||
        JSON.stringify(value)
      );
    }
    return value;
  };

  const statusTag = (status) => {
    const normalizedStatus =
      mapStatusForBackend(status) ||
      (status || "").toString().toUpperCase();
    switch (normalizedStatus) {
      case "APPROVED":
      case "PAID":
      case "SUCCESS":
        return <Tag color="green">Payé</Tag>;
      case "FAILED":
      case "ERROR":
        return <Tag color="red">Échoué</Tag>;
      case "EXPIRED":
      case "CANCELLED":
        return <Tag color="volcano">Expiré</Tag>;
      case "PENDING":
        return <Tag color="gold">En attente</Tag>;
      default:
        return <Tag>{status || "-"}</Tag>;
    }
  };

  const columns = [
    {
      title: "Référence",
      dataIndex: "reference",
      key: "reference",
      render: resolveText,
    },
    {
      title: "Numéro Assuré",
      dataIndex: "numeroAssure",
      key: "numeroAssure",
      render: resolveText,
    },
    {
      title: "Numéro Paiement",
      dataIndex: "numeroPaiement",
      key: "numeroPaiement",
      render: resolveText,
    },
    {
      title: "Montant (CFA)",
      dataIndex: "montant",
      key: "montant",
      render: formatAmount,
    },
    {
      title: "Réseau",
      dataIndex: "network",
      key: "network",
      render: resolveText,
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: statusTag,
    },
    {
      title: "Créé le",
      dataIndex: "createdAt",
      key: "createdAt",
      render: formatDateTime,
    },
    {
      title: "Mis à jour le",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: formatDateTime,
    },
    {
      title: "Créé par",
      dataIndex: "createdBy",
      key: "createdBy",
      render: resolveText,
    },
  ];

  const statusOptions = [
    { value: "APPROVED", label: "Payé / Succès" },
    { value: "FAILED", label: "Échoué / Erreur" },
    { value: "EXPIRED", label: "Expiré" },
    { value: "CANCELLED", label: "Annulé" },
    { value: "PENDING", label: "En attente" },
  ];

  const filteredData = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return datas;
    }
    return datas.filter((item) => {
      const haystack = [
        item.reference,
        item.numeroAssure,
        item.numeroPaiement,
        item.network,
        item.status,
        item.createdBy,
      ]
        .map(normalizeForSearch)
        .filter(Boolean)
        .join(" ");
      return haystack.includes(term);
    });
  }, [datas, search]);

  return (
    <ConfigProvider locale={frFR}>
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <SideBar currentMenu={"CAISSE"} />
          <div className="layout-page">
            <NavBar />
            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                <div className="card">
                  <div className="card-body">
                    {/* Titre ajouté */}
                    <h5 style={{ marginBottom: 16, fontWeight: "bold" }}>Liste des transactions</h5>
                    <Space style={{ marginBottom: 12 }} wrap>
                      <RangePicker onChange={(value) => setDates(value)} />
                      <Button type="primary" onClick={() => fetchDatas()}>Filtrer</Button>
                    </Space>

                    <ListControls
                      filters={[{ key: "status", label: "Statut", options: statusOptions }]}
                      onParamsChange={({ search: nextSearch, filters }) => {
                        const nextStatus = mapStatusForBackend(filters?.status);
                        setSearch(nextSearch || "");
                        setStatusFilter(nextStatus);
                        if (nextStatus !== statusFilter) {
                          fetchDatas({ status: nextStatus });
                        }
                      }}
                      exportFormats={[
                        { key: "csv", label: "CSV", onExport: () => handleExport("csv") },
                      ]}
                      isLoading={loading}
                    />
                    <Table
                      dataSource={filteredData}
                      columns={columns}
                      rowKey={(record) =>
                        record?.reference ||
                        record?.numeroPaiement ||
                        record?.code ||
                        record?.id ||
                        JSON.stringify(record)
                      }
                      loading={loading}
                      scroll={{ x: true }}
                      pagination={{ showSizeChanger: true }}
                    />
                  </div>
                </div>
              </div>
              <Footer />
            </div>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default TransactionsTontine;
